import { useCallback, useEffect, useMemo, useState } from 'react'
import { IPosition, IPositionType } from 'src/generated-types'
import { PositionConfig, QuickSelectMode } from 'src/generated-types/configure-positions'
import {
  SelectionByType,
  addIds,
  getQuickSelectIds,
  removeIds,
  toggleId,
  toggleIdGroup,
} from './ConfigurePositionsForm.action'

interface UseConfigureSelectionOptions {
  activeType: IPositionType | null
  positionsOfType: IPosition[]
  configById: Map<number, PositionConfig>
}

const isTextInput = (target: EventTarget | null) =>
  target instanceof HTMLElement && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')

export const useConfigureSelection = ({ activeType, positionsOfType, configById }: UseConfigureSelectionOptions) => {
  const [selectedByType, setSelectedByType] = useState<SelectionByType>({})

  const selectedIds = useMemo(
    () => new Set(activeType == null ? [] : selectedByType[activeType] ?? []),
    [activeType, selectedByType],
  )

  const updateTypeSelection = useCallback((type: IPositionType | null, update: (selectedIds: number[]) => number[]) => {
    if (type == null) return
    setSelectedByType(previous => ({ ...previous, [type]: update(previous[type] ?? []) }))
  }, [])

  const setTypeSelection = useCallback(
    (type: IPositionType, ids: number[]) => updateTypeSelection(type, () => ids),
    [updateTypeSelection],
  )

  const togglePosition = useCallback(
    (id: number, type: IPositionType) => updateTypeSelection(type, ids => toggleId(ids, id)),
    [updateTypeSelection],
  )

  const addToSelection = useCallback(
    (ids: number[]) => updateTypeSelection(activeType, selected => addIds(selected, ids)),
    [activeType, updateTypeSelection],
  )

  const removeFromSelection = useCallback(
    (ids: number[]) => updateTypeSelection(activeType, selected => removeIds(selected, ids)),
    [activeType, updateTypeSelection],
  )

  const toggleGroup = useCallback(
    (ids: number[]) => updateTypeSelection(activeType, selected => toggleIdGroup(selected, ids)),
    [activeType, updateTypeSelection],
  )

  const quickSelect = useCallback(
    (mode: QuickSelectMode) => {
      if (activeType == null) return
      setTypeSelection(activeType, getQuickSelectIds(mode, positionsOfType, configById))
    },
    [activeType, positionsOfType, configById, setTypeSelection],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || activeType == null || isTextInput(event.target)) return
      if ((selectedByType[activeType] ?? []).length === 0) return
      setTypeSelection(activeType, [])
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [activeType, selectedByType, setTypeSelection])

  return {
    selectedByType,
    selectedIds,
    setTypeSelection,
    togglePosition,
    addToSelection,
    removeFromSelection,
    toggleGroup,
    quickSelect,
  }
}
